import { SearchClient } from 'algoliasearch'

import { VIEW_OPTION_MEMBERS_ONLY } from '../../constants'
import { Centre } from '../../../types/contentful-v1'
import { distancePerState } from '../../helpers/distance-per-state'

import {
  SearchContext,
  SearchParams,
  Index,
  Query,
  SegmentHits,
  Segment,
  RawResults,
  AlgoliaHit,
} from './types'

// The queries we run whenever text changes, and how the results turn into dom.
// Everything here is passed to algolia except for the render method.

const centreWithDateAndViewOptionFilter = (
  centreID: string,
  viewOptionToExclude: string,
  getOffsetlessTimeInCentre: () => number
): SearchParams => ({
  filters: `centres:${centreID} AND (NOT viewOption:"${viewOptionToExclude}") AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const countryWithDateAndViewOptionFilter = (
  country: string,
  viewOptionToExclude: string,
  getOffsetlessTimeInDefaultTz: () => number
): SearchParams => ({
  filters: `countries:"${country}" AND (NOT viewOption:"${viewOptionToExclude}") AND (showAt<${getOffsetlessTimeInDefaultTz()} AND hideAt>${getOffsetlessTimeInDefaultTz()})`,
})

const singleCentreWithDateFilter = (
  centreID: string,
  getOffsetlessTimeInCentre: () => number
): SearchParams => ({
  filters: `centre:${centreID} AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

interface CentreSegmentContext {
  country: string
  getOffsetlessTimeInDefaultTz: () => number
  centre: Centre
  getOffsetlessTimeInCentre: () => number
  isEvolved?: boolean
}

type GetSegmentParams = (context: SearchContext) => SearchParams | null

function isCentreSectionContext(
  context: SearchContext
): context is CentreSegmentContext {
  return Boolean(context?.centre) && Boolean(context?.getOffsetlessTimeInCentre)
}

function nullIfNotCentre(
  f: (context: CentreSegmentContext) => SearchParams
): GetSegmentParams {
  return function (context) {
    return isCentreSectionContext(context) ? f(context) : null
  }
}

const retailerFilter = ({
  country,
  isEvolved,
}: SearchContext): SearchParams | null =>
  isEvolved
    ? {
        filters: `hasMarketplaceShopSlug:true AND country:"${country}"`,
      }
    : null

const basicCentreFilter = nullIfNotCentre((context: CentreSegmentContext) =>
  singleCentreWithDateFilter(
    context.centre.sys.id,
    context.getOffsetlessTimeInCentre
  )
)

const justCountryFilter = ({ country }: SearchContext): SearchParams => ({
  filters: `country:"${country}"`,
})

const viewOptionCentreFilter = nullIfNotCentre(
  (context: CentreSegmentContext) =>
    centreWithDateAndViewOptionFilter(
      context.centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      context.getOffsetlessTimeInCentre
    )
)

const viewOptionCountryFilter = (context: SearchContext): SearchParams =>
  countryWithDateAndViewOptionFilter(
    context.country,
    VIEW_OPTION_MEMBERS_ONLY,
    context.getOffsetlessTimeInDefaultTz
  )

const otherStoresFilter = nullIfNotCentre((context: CentreSegmentContext) => {
  const { centre } = context
  return {
    aroundLatLng: `${centre.latitude}, ${centre.longitude}`,
    facetFilters: `centre:-${centre.sys.id}`,
    aroundRadius: distancePerState(centre.state), // in meters
  }
})

const makeMarketplaceFilter = (params: SearchParams): GetSegmentParams =>
  function marketplaceFilter(context) {
    return context?.isEvolved && context.country === 'Australia' ? params : null
  }

type SegmentParams = Record<Segment, GetSegmentParams>
type SegmentIndexes = Record<Segment, Index>

const segmentParams: SegmentParams = {
  retailers: retailerFilter,
  stores: basicCentreFilter,
  sites: nullIfNotCentre(() => ({})),
  services: basicCentreFilter,
  offers: viewOptionCentreFilter,
  events: viewOptionCentreFilter,
  news: viewOptionCentreFilter,
  localStories: viewOptionCentreFilter,
  nationalStories: viewOptionCountryFilter,
  centres: justCountryFilter,
  onlineServices: () => ({}),
  nearCentreStores: otherStoresFilter,
  products: makeMarketplaceFilter({
    hitsPerPage: 1000,
    filters: `canFormSingleVariant: "true"`,
  }),
  categories: makeMarketplaceFilter({ removeWordsIfNoResults: 'allOptional' }),
}

const segmentIndexes: SegmentIndexes = {
  retailers: 'retailer',
  stores: 'store',
  sites: 'site',
  services: 'service',
  offers: 'offer',
  events: 'event',
  news: 'news',
  localStories: 'story',
  nationalStories: 'story',
  centres: 'centre',
  onlineServices: 'online_service',
  nearCentreStores: 'store',
  products: 'products_au',
  categories: 'categories_au',
}

export interface Clients {
  content: SearchClient
  marketplace: SearchClient
}

const whichClient = (segment: Segment): 'marketplace' | 'content' =>
  ['products', 'categories'].includes(segment) ? 'marketplace' : 'content'

interface Queries {
  content: Query<Index>[]
  marketplace: Query<Index>[]
}

export default async function querySegments(
  queryText: string,
  clients: Clients,
  context: SearchContext,
  segmentsToQuery: Segment[]
): Promise<SegmentHits> {
  const queries: Queries = { content: [], marketplace: [] }
  const segments: { content: Segment[]; marketplace: Segment[] } = {
    content: [],
    marketplace: [],
  }
  segmentsToQuery.forEach(segment => {
    const params = segmentParams[segment](context)
    if (params) {
      const indexName = segmentIndexes[segment]
      queries[whichClient(segment)].push({
        query: queryText,
        params,
        indexName,
      })
      segments[whichClient(segment)].push(segment) // record the shape for the response
    }
  })
  const [contentResponse, marketplaceResponse] = await Promise.all([
    clients.content.search(queries.content),
    clients.marketplace.search(queries.marketplace),
  ])
  const processedResults: SegmentHits = {}
  const evolvedOnlyCheck = (hit: AlgoliaHit): boolean =>
    context.isEvolved || !hit.isEvolvedOnly
  contentResponse.results.forEach(({ hits }: RawResults, i: number) => {
    const segment = segments.content[i]
    processedResults[segment] = hits.filter(evolvedOnlyCheck)
  })
  marketplaceResponse.results.forEach(({ hits }: RawResults, i: number) => {
    const segment = segments.marketplace[i]
    processedResults[segment] = hits.filter(evolvedOnlyCheck)
  })
  return processedResults
}
